import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';
import { Spinner } from '../ui/primitives';
import { EmptyState } from '../ui/states';
import { LimitsGate } from '../hooks/LimitsProvider';
import { LegalGate } from '../ui/Legal';
import { entryPath, slugOfBarePath } from './entryPaths';
import { useSession } from './SessionProvider';
import type { PermissionKey } from './permissions';

/**
 * Holds a route until the session is known, then sends an unauthenticated visitor to sign in.
 *
 * Where a user holds several memberships, they land on the chooser instead: they are authenticated, but
 * until they say which nursery they are in there is no tenant to fetch anything from (D-2).
 *
 * Once signed in, it loads the configured limits before rendering, so every screen under it can call
 * `useLimits` and none keeps a number of its own (Q48).
 *
 * With `slugEntry` (the three nursery apps), a signed-out visitor on a bare `/nuvakids` is sent to that
 * nursery's sign-in, which falls back to the plain one, with the same destination, where the word names no
 * nursery (`/account`, say).
 */
export function RequireSession({ children, slugEntry = false }: { children: ReactNode; slugEntry?: boolean }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <RouteSpinner />;
  if (status === 'selecting') return <Navigate to="/choose" replace />;
  if (status !== 'authenticated') {
    const slug = slugEntry ? slugOfBarePath(location.pathname) : null;
    // `from` brings them back to the page they asked for once they are in, rather than to a generic home.
    return <Navigate to={entryPath('sign-in', slug)} replace state={{ from: location.pathname + location.search }} />;
  }
  // A legal text published since the person last accepted is accepted before anything else (Q67).
  return <LimitsGate><LegalGate>{children}</LegalGate></LimitsGate>;
}

/** Sends an already-signed-in visitor away from sign-in rather than showing them the form again. */
export function RequireAnonymous({ children, to = '/' }: { children: ReactNode; to?: string }) {
  const { status } = useSession();
  if (status === 'loading') return <RouteSpinner />;
  if (status === 'authenticated') return <Navigate to={to} replace />;
  return <>{children}</>;
}

export interface RequirePermissionProps {
  /** Held together with every other in the list. */
  all?: PermissionKey[];
  /** At least one of these. */
  any?: PermissionKey[];
  children: ReactNode;
  /** Shown instead of the refusal notice — for a nested section that should simply not appear. */
  fallback?: ReactNode;
}

/**
 * Renders its children only where the caller holds the permissions.
 *
 * This is presentation, not enforcement: the same keys are checked again on every request. What it buys is
 * a user never being shown a control that would be refused, which matters most for staff, who must not see a
 * financial field anywhere in the application (FR-STF-11).
 */
export function RequirePermission({ all, any, children, fallback }: RequirePermissionProps) {
  const { canAll, canAny } = useSession();
  const { t } = useI18n();

  const permitted = (all ? canAll(...all) : true) && (any ? canAny(...any) : true);
  if (permitted) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;

  return <EmptyState tone="warning" title={t('error.forbidden')} />;
}

function RouteSpinner() {
  const { t } = useI18n();
  return (
    <div className="dg-route-spinner">
      <Spinner label={t('state.loading')} />
    </div>
  );
}
